import React, { useState } from "react";
import admin_users_model_base from "models/admin_users_model_base";
import { Button, Checkbox, Dimmer, Form, Loader, Message, Table, TextArea } from "semantic-ui-react";
import { useAsync, useAsyncUpdate } from "../Hooks/useAsync";
import { DateTime } from "@iscr/views/UIelems/DateTime";
import { rejectWithResErr } from "@iscr/xAppLib/helpers/rejectWithResErr";
import ListPagination from "@iscr/xAppLib/DataTable/ListPagination";

const admin_buttons_data = [
	{ n: "Email", value: "email" },
	{ n: "SMS", value: "sms" },
];

const INITIAL_CHECKED = { email: false, sms: false };
const AdminContactUser = ({ uid, mobile }) => {
	const [pagination, setPagination] = useState({ page: 1, limit: 5 });
	const setPage = page => setPagination(prev => ({ ...prev, page }));
	const setLimit = limit => setPagination({ page: 1, limit });

	const [checked, setChecked] = useState(INITIAL_CHECKED);
	const [msg, setMsg] = useState("");
	const SMS_CHAR_LIMIT = 245;

	const {
		data,
		loading: loadingMessages,
		error,
		fn: fetchMessages,
	} = useAsync(
		{
			immediate: true,
			fn: async () => await admin_users_model_base.get_user_messages({ uid, ...pagination }).then(rejectWithResErr),
		},
		[uid, pagination],
	);

	const handleCheckbox = type => (e, data) => {
		setChecked(prev => ({ ...prev, [type]: data.checked }));
	};

	const submit = useAsyncUpdate({
		keep: false,
		fn: async e => {
			e.preventDefault();
			const selectedTypes = Object.keys(checked).filter(type => checked[type]);
			// If SMS is selected, enforce character limit
			if (selectedTypes.includes("sms") && msg.length > SMS_CHAR_LIMIT) {
				return Promise.reject(new Error("SMS message exceeds character limit"));
			}
			if (selectedTypes.length === 0) {
				return Promise.reject(new Error("No message types selected"));
			}
			const result = await admin_users_model_base.send_message(uid, msg, selectedTypes).then(rejectWithResErr);
			setMsg("");
			setChecked(INITIAL_CHECKED);
			setPage(1);

			return result;
		},
	});

	// Filter out SMS if mobile is undefined
	const filteredButtons =
		mobile === undefined ? admin_buttons_data.filter(btn => btn.value !== "sms") : admin_buttons_data;

	return (
		<>
			<Form onSubmit={submit.fn}>
				<Form.Group inline>
					{filteredButtons.map((btn, idx) => (
						<Form.Field key={idx}>
							<Checkbox
								label={btn.n}
								checked={checked[btn.value]}
								onChange={handleCheckbox(btn.value)}
								disabled={submit.loading}
							/>
						</Form.Field>
					))}
				</Form.Group>
				<Form.Field>
					<TextArea
						placeholder="Enter your message"
						value={msg}
						disabled={submit.loading}
						onChange={e => {
							// If SMS is checked, limit to 245 chars
							if (checked.sms) {
								setMsg(e.target.value.slice(0, SMS_CHAR_LIMIT));
							} else {
								setMsg(e.target.value);
							}
						}}
						maxLength={checked.sms ? SMS_CHAR_LIMIT : undefined}
					/>
					{checked.sms && (
						<div
							style={{ marginTop: 4, fontSize: 12, color: msg.length > SMS_CHAR_LIMIT ? "red" : "#666" }}
						>
							{msg.length} / {SMS_CHAR_LIMIT} characters used ({SMS_CHAR_LIMIT - msg.length} remaining)
						</div>
					)}
				</Form.Field>
				<Button
					type="submit"
					primary
					loading={submit.loading}
					disabled={submit.loading || (!checked.email && !checked.sms) || !msg}
				>
					Send Message
				</Button>
				{submit.error ? (
					<Message
						visible
						error
						header="Error sending message"
						content={submit.error.message || String(submit.error)}
					/>
				) : submit.data ? (
					<Message visible success header="Message sent" />
				) : null}
			</Form>
			<div className="mt-8">
				<h4 className="text-lg font-semibold mb-4">Message History</h4>
				<div>
					{!data && loadingMessages ? (
						<div className="text-center py-8 text-gray-500">Loading messages…</div>
					) : error ? (
						<Message
							visible
							error
							header="Error loading messages"
							content={
								<p>
									<div>{error.message || String(error)}</div>
									<Button type="button" onClick={fetchMessages} basic>
										Retry
									</Button>
								</p>
							}
						/>
					) : (
						<div className="relative">
							<Dimmer active={loadingMessages} inverted>
								<Loader />
							</Dimmer>

							<Table>
								<Table.Header>
									<Table.Row>
										<Table.HeaderCell>Date</Table.HeaderCell>
										<Table.HeaderCell>Type</Table.HeaderCell>
										<Table.HeaderCell>Sent By</Table.HeaderCell>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									{(data?.items?.length ?? 0) > 0 ? (
										data.items.map(item => (
											<React.Fragment key={item.msg_id}>
												<Table.Row>
													<Table.Cell>
														<DateTime value={item.cre_tm} />
													</Table.Cell>
													<Table.Cell>{item.type.join(", ")}</Table.Cell>
													<Table.Cell>{item.meta?.by?.name || ""}</Table.Cell>
												</Table.Row>
												<Table.Row>
													<Table.Cell colSpan={3} className="!border-t-0 ">
														{item.message}
													</Table.Cell>
												</Table.Row>
											</React.Fragment>
										))
									) : (
										<Table.Row>
											<Table.Cell colSpan={3} className="text-center">
												No messages found.
											</Table.Cell>
										</Table.Row>
									)}
								</Table.Body>
							</Table>
							{data?.total > 0 && (
								<ListPagination
									data
									total={data.total}
									pages={data.pages}
									page={pagination.page}
									onPageChange={setPage}
									pageSize={pagination.limit}
									onPageSizeChange={setLimit}
								/>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default AdminContactUser;
